import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { transactionsApi, type ProposeTradePayload } from '@/api/transactions.api'
import type { Trade, TradeStatus } from '@/types/domain'

/**
 * Trades for a single league. The backend owns every trade/roster rule (ownership, legality,
 * pending-state transitions): this store loads trades, sends propose/accept/reject/cancel intents,
 * and surfaces backend errors (thrown to callers). A live `TradeProposed`/`TradeResolved` push
 * (from the notification store) upserts the affected trade so the list stays current.
 */
export const useTradeStore = defineStore('trade', () => {
  const trades = ref<Trade[]>([])
  const loading = ref(false)

  /** Returns trades incoming to the given team (it is the receiver) and still pending. */
  function incoming(teamId: string | null): Trade[] {
    if (!teamId) return []
    return trades.value.filter((t) => t.receivingTeamId === teamId && t.status === 'Proposed')
  }

  /** Returns trades outgoing from the given team (it proposed) and still pending. */
  function outgoing(teamId: string | null): Trade[] {
    if (!teamId) return []
    return trades.value.filter((t) => t.proposingTeamId === teamId && t.status === 'Proposed')
  }

  /** Resolved/historical trades (anything not pending). */
  const resolved = computed(() => trades.value.filter((t) => t.status !== 'Proposed'))

  /** Loads trades (member sees all). Optional team/status filter. */
  async function loadTrades(
    leagueId: string,
    filter: { teamId?: string; status?: TradeStatus } = {},
  ): Promise<void> {
    loading.value = true
    try {
      trades.value = await transactionsApi.listTrades(leagueId, filter)
    } finally {
      loading.value = false
    }
  }

  /** Inserts a new trade or replaces an existing one by id. */
  function upsert(trade: Trade): void {
    const idx = trades.value.findIndex((t) => t.id === trade.id)
    if (idx >= 0) trades.value[idx] = trade
    else trades.value = [trade, ...trades.value]
  }

  /**
   * Propose a trade. Errors (`player-not-owned`, `invalid-trade`, `self-trade`, `invalid-roster`)
   * propagate to the caller. The returned trade is added to the list.
   */
  async function proposeTrade(leagueId: string, payload: ProposeTradePayload): Promise<Trade> {
    const trade = await transactionsApi.proposeTrade(leagueId, payload)
    upsert(trade)
    return trade
  }

  /** Accept an incoming trade (executes; rosters swap). Errors propagate. */
  async function acceptTrade(leagueId: string, tradeId: string): Promise<Trade> {
    const trade = await transactionsApi.acceptTrade(leagueId, tradeId)
    upsert(trade)
    return trade
  }

  /** Reject an incoming trade (receiver or commissioner). */
  async function rejectTrade(leagueId: string, tradeId: string): Promise<Trade> {
    const trade = await transactionsApi.rejectTrade(leagueId, tradeId)
    upsert(trade)
    return trade
  }

  /** Cancel an outgoing trade (proposer). */
  async function cancelTrade(leagueId: string, tradeId: string): Promise<Trade> {
    const trade = await transactionsApi.cancelTrade(leagueId, tradeId)
    upsert(trade)
    return trade
  }

  function reset(): void {
    trades.value = []
    loading.value = false
  }

  return {
    // state
    trades,
    loading,
    // derived
    resolved,
    incoming,
    outgoing,
    // actions
    loadTrades,
    proposeTrade,
    acceptTrade,
    rejectTrade,
    cancelTrade,
    upsert,
    reset,
  }
})
